import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import { color, tooltipStyle, legendStyle } from "./palette";
import { GlassDefs, glassFill, GLASS_STROKE } from "./GlassDefs";
import { useNarrow } from "../../hooks/use-narrow";

function Panel({ title, subtitle, children, testid }) {
  return (
    <section className="glass-panel p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="font-display font-bold text-lg tracking-tight">{title}</h3>
          {subtitle && (
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

/**
 * Reusable colourful donut / pie chart card.
 * Props:
 *   title, subtitle, testid       — panel chrome
 *   data: [{name, value}]
 *   height: tailwind class height (default "h-72")
 *   inner, outer: pie radii (donut when inner > 0; full pie when inner = 0)
 *   labelMinPct: hide slice labels below this fraction (default 0.06)
 *   showLegend: default true
 *   emptyText: shown when data is empty
 */
export default function PieChartCard({
  title,
  subtitle,
  testid,
  data,
  height = "h-60 sm:h-72",
  inner = 55,
  outer = 100,
  labelMinPct = 0.06,
  showLegend = true,
  emptyText = "No data yet",
}) {
  const narrow = useNarrow();
  const hasData = Array.isArray(data) && data.length > 0;
  return (
    <Panel title={title} subtitle={subtitle} testid={testid}>
      <div className={height}>
        {!hasData ? (
          <div className="h-full flex items-center justify-center text-sm text-neutral-500 font-mono uppercase tracking-wider">
            {emptyText}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <GlassDefs />
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius={inner ? (narrow ? "42%" : "48%") : 0}
                outerRadius={narrow ? "72%" : "82%"}
                paddingAngle={2}
                label={
                  narrow
                    ? false
                    : ({ name, percent }) =>
                        percent > labelMinPct ? `${name} ${(percent * 100).toFixed(0)}%` : ""
                }
                labelLine={false}
                style={{ fontSize: 11 }}
                isAnimationActive
              >
                {data.map((entry, i) => (
                  <Cell key={entry.name} fill={glassFill(i)} stroke={GLASS_STROKE} strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v, n) => [`${v}`, n]}
              />
              {showLegend && (
                <Legend
                  layout={narrow ? "horizontal" : "vertical"}
                  align={narrow ? "center" : "right"}
                  verticalAlign={narrow ? "bottom" : "middle"}
                  wrapperStyle={
                    narrow
                      ? { ...legendStyle, fontSize: 9, lineHeight: "16px" }
                      : { ...legendStyle, paddingLeft: 16, lineHeight: "20px", maxWidth: "40%" }
                  }
                />
              )}
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </Panel>
  );
}
